import React from "react";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Form } from "tsi-common-react/src/forms/Form";
import { FormNumber } from "tsi-common-react/src/forms/FormNumber";
import { FormSelect } from "tsi-common-react/src/forms/FormSelect";
import { FormSubmit } from "tsi-common-react/src/forms/FormSubmit";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";
import { t } from "ttag";

interface IProps {}

interface IState {
    paymentAmount: string;
    paymentTerm: string;
    paymentAmountCalcd: string;
    paymentTermCalcd: string;
    paymentTotal: string;
}

// TODO(ssr, LEVEL 6 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 6/10 due to complex state with 5 fields, componentDidMount with DOM queries, form handling with multiple change handlers, and calculation logic.
export class FinanceCalculator extends React.Component<IProps, IState> {
    public state: IState = {
        paymentAmount: "4000",
        paymentTerm: "24",
        paymentAmountCalcd: "4000.00",
        paymentTermCalcd: "24",
        paymentTotal: "83.33",
    };

    private readonly onCalcPayment = (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        this.setState({
            paymentTotal: format.money(
                getMonthlyPrice(
                    getDinero(this.state.paymentAmount),
                    parseInt(this.state.paymentTerm, 10),
                ),
            ),
            paymentAmountCalcd: String(
                parseInt(this.state.paymentAmount, 10).toFixed(2),
            ),
            paymentTermCalcd: this.state.paymentTerm,
        });
        return Promise.resolve();
    };

    private readonly onPaymentChange = (
        event: React.FormEvent<HTMLInputElement>,
    ) => {
        this.setState({
            paymentAmount: event.currentTarget.value,
        });
    };

    private readonly onTermChange = (
        event: React.FormEvent<HTMLSelectElement>,
    ) => {
        this.setState({
            paymentTerm: event.currentTarget.value,
        });
    };

    componentDidMount() {
        const plans = listFinancingPlans();
        if (plans.length > 0) {
            const bestPlan = plans[plans.length - 1];
            const bestPlanLength = isoMonths.unwrap(bestPlan.length);

            this.setState({
                paymentTerm: String(bestPlanLength),
                paymentTermCalcd: String(bestPlanLength),
                paymentTotal: format.money(
                    getMonthlyPrice(
                        getDinero(this.state.paymentAmount),
                        bestPlanLength,
                    ),
                ),
            });
        }
    }

    private getTermMonths() {
        const plans = listFinancingPlans();

        return plans.map((plan) => {
            const planLength = isoMonths.unwrap(plan.length);

            return {
                // TODO(i18n): Use ngettext for plurals: ngettext(msgid`${planLength} Month`, `${planLength} Months`, planLength)
                label: `${planLength} Months`,
                value: `${planLength}`,
            };
        });
    }

    render() {
        return (
            <div className="finance-calculator">
                <h3 className="finance-calculator__title">
                    {t`Monthly Payment Calculator`}
                </h3>
                <Form
                    className="finance-calculator-form"
                    onSubmit={this.onCalcPayment}
                >
                    <fieldset>
                        <legend className="ada-screenreader-only">
                            {t`Calculate Monthly Payment`}
                        </legend>
                        <FormNumber
                            name="finance-payment"
                            // TODO(i18n): Wrap in t tagged template: label={t`Enter Financed Amount`}
                            label="Enter Financed Amount"
                            id="finance-payment"
                            className="finance-calculator-form__payment"
                            onChange={this.onPaymentChange}
                            value={this.state.paymentAmount}
                            min={1}
                            max={1000000}
                        />
                        <p className="finance-calculator-form__for">{t`for`}</p>
                        <FormSelect
                            name="finance-terms"
                            id="finance-terms"
                            // TODO(i18n): Wrap in t tagged template: label={t`Term Length Dropdown`}
                            label="Term Length Dropdown"
                            className="finance-calculator-form__terms"
                            value={this.state.paymentTerm}
                            onChange={this.onTermChange}
                            choices={this.getTermMonths()}
                        />
                        <FormSubmit
                            // TODO(i18n): Wrap in t tagged template: value={t`Calculate Monthly Payment`}
                            value="Calculate Monthly Payment"
                            className="finance-calculator-form__submit al-financing-page__calculator--calc-payment"
                        />
                    </fieldset>
                </Form>
                <div className="finance-calculator__payment">
                    {/* TODO(i18n): Use jt to keep sentence together with interpolated components instead of splitting into fragments */}
                    <a href="#terms_and_conditions">
                        <span className="finance-calculator__payment--underlined">
                            {this.state.paymentTotal}/{t`mo`}.
                        </span>
                        <sup>2</sup>
                        <span className="ada-screenreader-only">
                            {t`Additional information about Terms and Conditions`}
                        </span>
                    </a>
                    &nbsp;{t`for`}&nbsp;
                    <a href="#terms_and_conditions">
                        <span className="finance-calculator__payment--underlined">
                            {/* TODO(i18n): Use ngettext for plurals: ngettext(msgid`${this.state.paymentTermCalcd} month`, `${this.state.paymentTermCalcd} months`, parseInt(this.state.paymentTermCalcd, 10)) */}
                            {this.state.paymentTermCalcd} {t`months`}
                        </span>
                        <sup>1</sup>
                        <span className="ada-screenreader-only">
                            {t`Additional information about Terms and Conditions`}
                        </span>
                    </a>
                    <br />
                    {t`(total payments of $${this.state.paymentAmountCalcd})`}
                </div>
                <p className="finance-calculator__disclaimer">
                    {t`Check table for details about minimum financing requirements`}
                </p>
                <FinancingModalTrigger
                    modalType="prequal-app"
                    className="button button--inverse finance-calculator__button al-financing-page__calculator--check-prequalify"
                >
                    {t`Check if you Pre-Qualify`}
                </FinancingModalTrigger>
            </div>
        );
    }
}
